<template>
    <Attr :widget_id="widget_id" :attr_id="attr_id">
        <div class="attr-md"
            v-wjTooltip="attr.tooltip"
            v-wjContextMenu="'ContextWidgetMenuId'"
        >
            <label class="label-attr" v-if="attr.title && !attr.hideTitle" :for="attr.html_id">{{ attr.title }}</label>

            <!-- IE 2026 09 28 / ODOO-342342: у читалки с меню (fe.help.docsSearch) строка шапки прилипает ПОД полосой вкладок виджета (docsTopLineStyle): иначе после перехода к разделу
                 Back / Contents / ≡ уходили под липкую полосу вкладок (обе липли к top: 0, у вкладок z-index выше) -->
            <div class="grid-top-line md-top-line" ref="attr_top_line" v-if="!attr?.hideCommandPanel" :style="docsTopLineStyle">
                <div class="commandPanel-area" ref="commandPanelArea"
                    :style="commandPanelAreaWidth != null ? { flex: '0 1 ' + commandPanelAreaWidth + 'px' } : (!commandPanel.length && !commandPanelOverflow.length ? { flex: '0 0 0', minWidth: '0', overflow: 'hidden' } : {})"
                >
                    <div class="commandPanel-menu" v-if="commandPanel.length && commandPanelMaxVisible !== 0" ref="commandPanelMenu"
                        :style="commandPanelMenuWidth != null ? { flex: '0 0 ' + commandPanelMenuWidth + 'px' } : {}"
                    >
                        <span v-for="(attrM, index) in commandPanel"
                            :key="attrM.html_id"
                            class="cp-item-wrap"
                            v-show="index < commandPanelMaxVisible"
                        >
                            <component
                                :widget_id="widget_id"
                                :is="attrM.component"
                                :attr_id="attrM.id"
                                :initialAttr="attrM"
                            />
                        </span>
                    </div>
                    <div class="widget_menu commandPanel-overflow-btn" v-if="commandPanelOverflow.length">
                        <wj-menu
                            class="commandPanel-overflow-menu"
                            :header="''"
                            :displayMemberPath="'titleAction'"
                            :subItemsPath="'attrs'"
                            :showDropDownButton="false"
                            :openOnHover="true"
                            :isAnimated="false"
                            :itemsSource="commandPanelOverflow"
                            :itemClicked="menuNodeClicked"
                            :formatItem="store.formatMenuItem"
                        >
                            <i class="pi pi-ellipsis-v"></i>
                        </wj-menu>
                    </div>
                </div>

                <div class="grid-chips" v-if="!attr?.hideCHIPS">
                    <AttrChips :widget_id="widget_id" :attr_id="attr_id" :modelValue="chips_list" @update:modelValue="onChipsUpdate" :mode="'Legacy'" />
                </div>

                <!-- IE 2026 09 28 / ODOO-342342 (Igor: «"меню" — должно быть прижато вправо и использовать форму меню как на скрин2»; «добавь в элемент-инструкции поиск как в форме-меню»):
                     у читалки документации ≡ — справа в строке шапки и открывает форму MenuPanel, как ≡ таблицы: поиск, «Show rows without matches», группы Contents · «Все инструкции» ·
                     Command panel, при запросе — «Found in text» (utils/docsSearch.js); кнопка «Contents» открывает её же. fe.help.docsSearch = false — прежнее wj-menu -->
                <div class="widget_menu mr-2" :class="{ 'attrmd-docs-menu-host': docsMenuOn }">
                    <MenuPanel v-if="docsMenuOn" ref="docsMenu" :items="docsMenuItems" align="right" :caret="false" btnClass="widget-menu-btn attrmd-docs-menu-btn" panelClass="attrmd-docs-menu"
                        :title="t('Documentation menu')" titleIcon="pi pi-bars" :subtitle="docsMenuSubtitle" :openDelay="200" :colWidth="260" :splitTall="true"
                        search :searchPlaceholder="t('Search in guides…')" :searchExtra="docsSearchExtra" :currentOf="docsMenuCurrentOf"
                        @pick="docsMenuPick" @open="docsMenuOpened">
                        <template #trigger><i class="pi pi-bars pi-bold"></i></template>
                    </MenuPanel>
                    <wj-menu id="actionMenuId" :displayMemberPath="'titleAction'" :subItemsPath="'attrs'" :showDropDownButton="false"
                        v-else-if="actionMenu.length"
                        :openOnHover="true" :isAnimated="false" :itemsSource="actionMenu" :itemClicked="menuNodeClicked" :formatItem="store.formatMenuItem"
                    >
                        <i class="pi pi-bars pi-bold"></i>
                    </wj-menu>
                </div>
            </div>

            <component v-if="mode === 'view'" ref="preview" :is="mdWrapper" />

            <Codemirror v-else
                :id="attr.html_id"
                v-model:value="content_str"
                v-visible="handleVisibility"
                :options="cmOptions"
                @change="attr_set"
                @ready="editorReady"
            />
        </div>
    </Attr>
</template>

<script>
//  [[view.field.name:product_photo]]
//  [[record:{"id":"...","segment":"...","version":"...","title":"..."}]]

import { useMainStore } from '@/stores/mainStore'
import { h, defineComponent, markRaw } from 'vue'
import { docsNav_on, docsNav_open, docsNav_back, docsNav_contents, docsNav_buttons, docsNav_count } from '@/utils/docsNav'   // IE 2026 09 28 / ODOO-342342
// IE 2026 09 28 / ODOO-342342: меню читалки с поиском по инструкциям (форма MenuPanel) — флаг fe.help.docsSearch, логика utils/docsSearch.js
import { docsNav_jump, docsNav_isDocs, docsNav_norm, docsNav_path, docsNav_contentsLink, docsSearch_on } from '@/utils/docsNav'
import { docsSearch_headings, docsSearch_cached, docsSearch_ensure, docsSearch_find, docsSearch_localize, docsSearch_count, docsSearch_noteFallback,
    docsMenu_contentsGroup, docsMenu_guidesGroup, docsMenu_textGroup, docsMenu_commandGroups, docsMenu_isPage } from '@/utils/docsSearch'
import { getLanguage } from '@/i18n'   // IE 2026 09 28 / ODOO-342342: язык индекса поиска — язык интерфейса (реактивно)

import MarkdownIt from 'markdown-it'
import markdownItContainer from 'markdown-it-container'
import markdownItTaskLists from 'markdown-it-task-lists'
import Prism from 'prismjs'

import Codemirror from "codemirror-editor-vue3";
import "codemirror/mode/markdown/markdown.js";
import "codemirror/theme/neo.css";
import "codemirror/addon/fold/foldcode";
import "codemirror/addon/fold/foldgutter";
import "codemirror/addon/fold/foldgutter.css";

export default {
    props: [
        'widget_id',
        'attr_id',
        'parentAttr', // объект вкладки от рендерящего AttrTab/AttrTabRow (tabs_noId)
    ],
    data: () => ({
        WIDGET: {},
        content_str: '',
        content_str0: '',
        count_empty_tab: 0,
        mode: 'view',
        md: null,
        mdWrapper: null,
        cmOptions: {
            mode: 'markdown',
            theme: "neo",
            lineNumbers: true,
            smartIndent: true,
            indentUnit: 4,
            foldGutter: true,
            gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
            styleActiveLine: true,
            lineWrapping: true,
        },

        commandPanel: [],
        actionMenu: [],
        chips_list: [],
        commandPanelMaxVisible: Infinity,
        commandPanelAreaWidth: null,
        commandPanelMenuWidth: null,
        ind_commandPanel: 0,
        docsIndexEpoch: 0,   // IE 2026 09 28 / ODOO-342342: растёт, когда пришёл индекс поиска читалки (пересчёт групп меню)
        docsStickyTop: 0,    // IE 2026 09 28 / ODOO-342342: высота липкой полосы вкладок над строкой шапки читалки (docsTopLineStyle)
    }),
    setup() {
        const store = useMainStore();
        return { store }
    },
    created() {
        this.WIDGET = this.store.findWidget(this.widget_id).WIDGET

        const md = new MarkdownIt({
            html: true,
            linkify: true,
            typographer: true,
        })
        md.use(markdownItTaskLists, { enabled: true, label: true })
        md.use(markdownItContainer, 'alert', {
            validate(params) {
                return params.trim().match(/^alert\{type=(\w+)\}$/)
            },
        })
        // Якоря разделов: «## Заголовок {#anchor}» → <h2 id="anchor">, без явного
        // {#...} id получается слагом заголовка. По этим id виджет документации
        // прокручивается к разделу той вкладки, с которой нажали «?» (anchorBinding).
        md.core.ruler.push('heading_anchors', (state) => {
            const tokens = state.tokens
            for (let i = 0; i < tokens.length; i++) {
                if (tokens[i].type !== 'heading_open') continue
                const inline = tokens[i + 1]
                if (!inline || inline.type !== 'inline') continue
                const m = inline.content.match(/\s*\{#([^}\s]+)\}\s*$/)
                let id
                if (m) {
                    id = m[1]
                    inline.content = inline.content.slice(0, m.index)
                    const kids = inline.children || []
                    for (let k = kids.length - 1; k >= 0; k--) {
                        if (kids[k].type === 'text' && /\{#[^}\s]+\}\s*$/.test(kids[k].content)) {
                            kids[k].content = kids[k].content.replace(/\s*\{#[^}\s]+\}\s*$/, '')
                            break
                        }
                    }
                } else {
                    id = inline.content.trim().toLowerCase()
                        .replace(/[^a-z0-9\u0400-\u04ff]+/g, '-').replace(/^-+|-+$/g, '')
                }
                if (id) tokens[i].attrSet('id', id)
            }
        })

        md.block.ruler.before('paragraph', 'attr_embed', (state, startLine, endLine, silent) => {
            const pos = state.bMarks[startLine] + state.tShift[startLine]
            const max = state.eMarks[startLine]
            const line = state.src.slice(pos, max).trim()
            const match = line.match(/^\[\[view\.field\.name:([a-zA-Z0-9_\-.]+)\]\]$/)
            if (!match) return false
            if (silent) return true
            const token = state.push('attr_embed', '', 0)
            token.attrName = match[1]
            token.map = [startLine, startLine + 1]
            state.line = startLine + 1
            return true
        })
        md.block.ruler.before('paragraph', 'record_embed', (state, startLine, endLine, silent) => {
            const pos = state.bMarks[startLine] + state.tShift[startLine]
            const max = state.eMarks[startLine]
            const line = state.src.slice(pos, max).trim()
            const match = line.match(/^\[\[record:(.+)\]\]$/)
            if (!match) return false
            let recordData
            const raw = match[1]
            try {
                recordData = JSON.parse(raw)
            } catch {
                const getField = (key) => raw.match(new RegExp(`"?${key}"?\\s*:\\s*"([^"]*)"`))?.[1] ?? ''
                const id = getField('id')
                if (!id) return false
                recordData = { id, title: getField('title'), segment: getField('segment'), version: getField('version') }
            }
            if (silent) return true
            const token = state.push('record_embed', '', 0)
            token.recordData = recordData
            token.map = [startLine, startLine + 1]
            state.line = startLine + 1
            return true
        })
        this._RecordChipComponent = markRaw(defineComponent({
            props: ['recordData', 'widget_id'],
            setup() { return { store: useMainStore() } },
            data: () => ({ isHovering: false }),
            computed: {
                pi() { return this.recordData?.pi || 'pi-file' },
                title() { return this.recordData?.title || String(this.recordData?.id || '') || this.t('(record)') },   // IE 2026 09 23 / ODOO-341407: i18n
                WIDGET() { return this.store.findWidget(this.widget_id)?.WIDGET },
            },
            methods: {
                onIconClick(e) {
                    e.stopPropagation()
                    const vueObj = this.WIDGET?.vueObj
                    if (!vueObj) return
                    const chipEl = e?.currentTarget
                    const widgetEl = document.getElementById('WIDGET' + this.WIDGET.id)
                    let x, y
                    if (chipEl && widgetEl) {
                        const cr = chipEl.getBoundingClientRect()
                        const wr = widgetEl.getBoundingClientRect()
                        x = cr.left - wr.left + cr.width / 2
                        y = cr.top - wr.top + cr.height / 2
                    }
                    this.store.executeStoreMethod(vueObj, {
                        command: 'widget_open',
                        widget: 'widget-row',
                        type: 'widget-row-record-embed',
                        open_in_children_widget: true,
                        item: {
                            id: this.recordData.id,
                            segment: this.recordData.segment,
                            version: this.recordData.version || '',
                            title: this.recordData.title || this.recordData.id,
                            _meta: { type: 'widget-row-record-embed' },
                            _record: this.recordData,
                        },
                        widgetRow_params: { top_pct: 0.6, left_pct: 0.2, width_pct: 0.8, height_pct: 0.8 },
                        selectedCog: { x, y },
                    })
                },
                onTextClick(e) {
                    e.stopPropagation()
                    const vueObj = this.WIDGET?.vueObj
                    if (!vueObj) return
                    this.store.executeStoreMethod(vueObj, {
                        command: 'widget_open',
                        widget: 'widget-record',
                        item: {
                            id: this.recordData.id,
                            segment: this.recordData.segment,
                            version: this.recordData.version,
                            title: this.recordData.title,
                        },
                    })
                },
            },
            render() {
                return h('span', {
                    class: 'md-record-chip',
                    onMouseover: () => { this.isHovering = true },
                    onMouseleave: () => { this.isHovering = false },
                }, [
                    h('span', {
                        class: ['md-record-chip-icon', 'pi', this.isHovering ? 'pi-cog' : this.pi],
                        onClick: this.onIconClick,
                    }),
                    h('span', { class: 'md-record-chip-text', onClick: this.onTextClick }, this.title),
                ])
            },
        }))
        this.md = md

        const self = this
        this.mdWrapper = markRaw(defineComponent({
            setup() {
                return () => {
                    const vnodes = self._buildVNodes(self.md.parse(self.content_str || '', {}))
                    return h('div', {
                        id: self.attr?.html_id,
                        class: 'attrmd-preview',
                        onDblclick: self.switchToEdit,
                        onClick: self._onPreviewClick,   // IE 2026 09 28 / ODOO-342342: ссылки docs: в сыром HTML (плитки)
                    }, vnodes)
                }
            },
        }))

        this.content_Changed()
    },
    mounted() {
        this.WIDGET.attrsResize = this.WIDGET.attrsResize || []
        this.WIDGET.attrsResize.push(this.attrResize)
        this.$nextTick(() => {
            const container = this.$refs.commandPanelMenu
            const topLine = this.$refs.attr_top_line
            if (container && topLine && !this.commandPanelResizeObserver) {
                this.commandPanelResizeObserver = new ResizeObserver(() => this.updateCommandPanelOverflow())
                this.commandPanelResizeObserver.observe(topLine)
                const chipsEl = topLine.querySelector('.grid-chips')
                if (chipsEl) this.commandPanelResizeObserver.observe(chipsEl)
            }
            this.updateCommandPanelOverflow()
            this._docsStickyMeasure()   // IE 2026 09 28 / ODOO-342342
        })
    },
    beforeUnmount() {
        this.commandPanelResizeObserver?.disconnect()
        if (this._cpRafId) cancelAnimationFrame(this._cpRafId)
        if (this.WIDGET.attrsResize) {
            const idx = this.WIDGET.attrsResize.indexOf(this.attrResize)
            if (idx !== -1) this.WIDGET.attrsResize.splice(idx, 1)
        }
        this.store.attr_deregister(this)
    },
    methods: {
        content_Changed() {
            this.store.widget_attr_set(this)

            const [doc, binding] = this.store.attr_get_link(this.WIDGET, this.attr)
            this.doc = doc
            this.binding = binding
            this.content_str = this.doc[this.binding] || ''
            this.content_str0 = this.content_str

            this.set_commandPanel()
            this.$nextTick(() => this.apply_CHIPS())

            if (!this._tabFeatures_initialized && this.parent_attr) {
                this._tabFeatures_initialized = true
                if (this.parent_attr?.attr_type === 'AttrTab' && !this.parent_attr?.['hide-countItems']) {
                    this.attr['can-set_AttrTabPanel_countItems'] = true
                }
                this.attr_nonEmptyWidget = this.store.get_field0(this.WIDGET.groupMenu?.commandPanel, 'non-empty-widget', 'name')
                if (!this.attr.showEmpty && this.attr_nonEmptyWidget) {
                    this.WIDGET.applys_CHIPS[this.attr.id] = this
                }
            }
            this.check_attrTab()
            this.set_AttrTabPanel_countItems()
            this.scrollToAnchor()
            if (this.docsMenuOn) this._docsIndexLater()   // IE 2026 09 28 / ODOO-342342: индекс поиска — заранее (один запрос на базу · версию бэка · язык)
            this.$nextTick(this._docsStickyMeasure)   // IE 2026 09 28 / ODOO-342342: строка шапки читалки — под полосой вкладок
        },
        // attr.anchorBinding — путь в записи, где лежит id раздела (виджет
        // документации кладёт туда вкладку исходного виджета). Нет совпадения —
        // остаёмся вверху документа: это не ошибка, у раздела просто нет якоря
        scrollToAnchor() {
            // IE 2026 09 28 / ODOO-342342: читалка листается на месте (fe.help.inPlaceDocs) — цикл доводки прошлого документа (до 8 с)
            // держал бы ЕГО якорь в новом тексте; каждый вызов начинает своё поколение, прежний цикл выходит
            const gen = docsNav_on(this.store) ? (this._anchorGen = (this._anchorGen || 0) + 1) : null
            const path = this.attr?.anchorBinding
            if (!path) return
            const anchor = this.store.get_value(this.WIDGET.record, path)
            if (!anchor) return
            // IE 2026 09 24 / ODOO-341407: значение — строка или список кандидатов (вкладка вложенной панели → главной → якорь ссылки):
            // берём первый, для которого в тексте есть раздел
            const anchors = (Array.isArray(anchor) ? anchor : [anchor]).map(x => String(x || '')).filter(Boolean)
            if (!anchors.length) return
            // Раздел появляется в DOM не сразу, а высота скроллера виджета — ещё
            // позже: одного scrollIntoView мало (скроллить нечего, промах молчит),
            // поэтому доводим позицию, пока заголовок не встанет под верх области
            let tries = 0
            const tick = () => {
              if (gen !== null && gen !== this._anchorGen) return   // IE 2026 09 28 / ODOO-342342: документ сменился — цикл устарел
              try {
                // $el у компонента-обёртки может быть не элементом (комментарий
                // фрагмента) — тогда querySelector бросает и цикл молча умирает
                const root = (this.$el && this.$el.querySelector ? this.$el : null)
                             || document.getElementById(this.attr?.html_id)
                let el = null
                for (const a of anchors) { el = root?.querySelector(`[id="${CSS.escape(a)}"]`); if (el) break }   // IE 2026 09 24 / ODOO-341407
                if (el) {
                    let sc = el.parentElement
                    while (sc && !(sc.scrollHeight > sc.clientHeight + 2
                                   && /auto|scroll/.test(getComputedStyle(sc).overflowY))) {
                        sc = sc.parentElement
                    }
                    if (sc) {
                        const scRect = sc.getBoundingClientRect()
                        const delta = el.getBoundingClientRect().top - (scRect.top + this._stickyTop(sc, scRect))
                        // не выходим по первому попаданию: виджет доразмечается
                        // после открытия и сбрасывает scrollTop — держим позицию
                        if (Math.abs(delta) > 4) sc.scrollTop += delta
                    } else {
                        el.scrollIntoView({ block: 'start' })
                    }
                }
              } catch (e) { /* разметка ещё едет — просто пробуем ещё раз */ }
                if (++tries < 40) setTimeout(tick, 200)
            }
            this.$nextTick(() => setTimeout(tick, 100))
        },
        // Верх области прокрутки перекрыт липкой полосой вкладок виджета
        // (.wj-tabheaders, position: sticky) — выравнивание заголовка ровно по
        // верху скроллера прячет его ПОД панель: видно только нижние пиксели,
        // заголовок выглядит перечёркнутым. Меряем не по имени класса, а тем,
        // что реально нарисовано в верхней точке области, и поднимаемся до
        // первого липкого предка — так же сработает для любой другой панели.
        // IE 2026 09 28 / ODOO-342342: липкие полосы могут стоять стопкой (полоса вкладок и под ней — строка шапки читалки, docsTopLineStyle): проба
        // повторяется под найденной полосой, пока находятся липкие (не больше 4) — заголовок раздела встаёт под нижнюю. Без отступа строки шапки
        // (меню читалки выключено, не читалка) — одна проба, как было
        _stickyTop(sc, scRect) {
            let off = 0
            const tries = this.docsStickyTop > 0 ? 4 : 1
            for (let k = 0; k < tries; k++) {
                let next = off
                try {
                    const probe = document.elementFromPoint(
                        scRect.left + Math.min(40, scRect.width / 2), scRect.top + off + 2)
                    for (let e = probe; e && e !== sc; e = e.parentElement) {
                        const pos = getComputedStyle(e).position
                        if (pos === 'sticky' || pos === 'fixed') {
                            next = Math.max(off, e.getBoundingClientRect().bottom - scRect.top)
                            break
                        }
                    }
                } catch (e) { /* точка вне окна — считаем, что перекрытия нет */ }
                if (next <= off) break
                off = next
            }
            return Math.max(0, off)
        },
        // IE 2026 09 28 / ODOO-342342: высота липких полос НАД строкой шапки читалки (полоса вкладок виджета) — отступ её прилипания (docsTopLineStyle)
        _docsStickyMeasure() {
            if (!this.docsMenuOn) { if (this.docsStickyTop) this.docsStickyTop = 0; return }
            try {
                const top = this.$refs.attr_top_line
                let sc = top && top.parentElement
                while (sc && !/auto|scroll/.test(getComputedStyle(sc).overflowY)) sc = sc.parentElement
                if (!top || !sc) return
                const r = sc.getBoundingClientRect()
                if (r.height < 20) return
                const was = top.style.top
                top.style.top = '0px'   // замер без собственного отступа
                let h = 0
                for (const el of sc.querySelectorAll('.wj-tabheaders')) {
                    if (!el.contains(top) && el.compareDocumentPosition(top) & Node.DOCUMENT_POSITION_FOLLOWING && getComputedStyle(el).position === 'sticky') {
                        const b = el.getBoundingClientRect()
                        if (b.height > 0 && b.width > 0) h = Math.max(h, Math.round(b.height))
                    }
                }
                top.style.top = was
                if (h !== this.docsStickyTop) this.docsStickyTop = h
            } catch (e) { /* разметка ещё едет — отступ прежний */ }
        },
        attr_set() {
            if (this.content_str !== this.content_str0) {
                this.content_str0 = this.content_str
                this.store.attr_set(this.WIDGET, this.attr, this.content_str)
                this.check_attrTab()
                this.set_AttrTabPanel_countItems()
            }
        },
        tooltip() {
            return this.store.tooltip_attr(this)
        },
        _hide_empty() {
            return this.attr_nonEmptyWidget?.active && !this.attr_nonEmptyWidget?.hide && !this.attr.showEmpty
        },
        check_attrTab() {
            // родитель: при tabs_noId — структурно от рендерящей вкладки (parentAttr);
            // иначе parent_attr мог устареть после пересборки view — резолвим заново
            // (кроме initialAttr-копий: их attr_id указывает на чужой оригинал)
            if (this.store.tabs_noId && this.parentAttr) {
                this.parent_attr = this.parentAttr
            } else if (!this.initialAttr && this.attr_id != null) {
                const found = this.store.widget_attr(this.WIDGET, this.attr_id)
                if (found) { this.parent_attr = found.parent }
            }
            const parent_attr = this.parent_attr
            if (!parent_attr || (parent_attr.attr_type !== 'AttrTab' && parent_attr.attr_type !== 'AttrTabRow')) {
                return // hide управляет только видимостью вкладок
            }
            parent_attr._hideChecked = true   // IE 2026 09 06 / ODOO-336330: вердикт по факту — предварительная проверка панели (tabsPrecheck) его не трогает
            if (parent_attr.showEmpty) return
            const isEmpty = !this.content_str || !this.content_str.trim()
            if (parent_attr.attrs?.length === 1) {
                if (isEmpty && this._hide_empty()) {
                    parent_attr.hide = true
                    this.count_empty_tab = 1
                } else {
                    parent_attr.hide = false
                    this.count_empty_tab = 0
                }
            } else if (parent_attr.hide) {
                // вкладка больше не одиночная — прятать её некому, снимаем залипший hide
                parent_attr.hide = false
                this.count_empty_tab = 0
            }
        },
        set_AttrTabPanel_countItems() {
            if (this.set_AttrTabPanel_countItems_timerId) { clearTimeout(this.set_AttrTabPanel_countItems_timerId) }
            this.set_AttrTabPanel_countItems_timerId = window.$cg.setTimeout(this, "AttrMD.set_AttrTabPanel_countItems", () => {
                this.set_AttrTabPanel_countItems_afterTimeout()
                this.set_AttrTabPanel_countItems_timerId = null
            }, 100)
        },
        set_AttrTabPanel_countItems_afterTimeout() {
            if (this.attr?.['can-set_AttrTabPanel_countItems']) {
                let countItems = 0
                if (this.content_str && this.content_str.trim()) {
                    countItems = this.content_str.split('\n').filter(line => line.trim()).length
                }
                if (this.parent_attr.countItems !== countItems) {
                    this.parent_attr.countItems = countItems
                }
            }
        },
        apply_CHIPS(e=null, {check_nonEmptyWidget=true}={}) {
            this.check_attrTab()
            if (check_nonEmptyWidget) {
                this.store.check_nonEmptyWidget(this.WIDGET)
            }
            if (this.editor && this.mode === 'edit') {
                const searchChips = this.chips_list.filter(c => c.use !== false && c.segment === 'CHIP-Search')
                const cm = this.editor
                cm.operation(() => {
                    for (let i = 0; i < cm.lineCount(); i++) {
                        cm.removeLineClass(i, 'wrap', 'cm-line-filtered-out')
                    }
                    if (!searchChips.length) return
                    for (let i = 0; i < cm.lineCount(); i++) {
                        const lineText = cm.getLine(i).toLowerCase()
                        const matches = searchChips.some(chip => lineText.includes(chip.title.toLowerCase()))
                        if (!matches) {
                            cm.addLineClass(i, 'wrap', 'cm-line-filtered-out')
                        }
                    }
                })
            }
        },
        toggleMode() {
            if (this.mode === 'view') {
                this.mode = 'edit'
            } else {
                this.attr_set()
                this.mode = 'view'
            }
            this.set_commandPanel()
        },
        switchToEdit() {
            if (!this.isReadOnly) {
                this.mode = 'edit'
                this.set_commandPanel()
            }
        },
        editorReady(editor) {
            this.editor = editor
            this.attrResize()
        },
        handleVisibility(isVisible) {
            this.isVisible = isVisible
            if (isVisible && this.editor) {
                this.editor.refresh()
                this.attrResize()
            }
        },
        _attrsToProps(attrs) {
            if (!attrs?.length) return {}
            const obj = {}
            for (const [k, v] of attrs) obj[k] = v
            return obj
        },
        _openDocLink(e, link) {
            // Открываем виджет документации на другом файле. Команда целиком
            // приезжает на бэкенд как parentAction, а widget_DOCS читает из неё
            // params.doc_link — тот же путь, которым работает кнопка «?».
            // Якорь после '#' бэкенд возьмёт из самой ссылки.
            e.preventDefault()
            e.stopPropagation()
            const vueObj = this.WIDGET?.vueObj
            if (!vueObj || !link) return
            // markdown-it percent-кодирует ссылку, а якоря разделов у нас бывают
            // кириллические (id заголовка — сырой слаг). Без decode якорь не
            // совпал бы с id и документ открывался бы сверху.
            let href = link
            try { href = decodeURIComponent(link) } catch (err) { /* оставляем как есть */ }

            // IE 2026 09 28 / ODOO-342342: читалка документации (панель widget-docs или запись WIDGET-DOCS на странице) листает сама себя —
            // метод бэка open_doc, прежний документ уходит в историю (кнопка Back). Флаг fe.help.inPlaceDocs выключен, не читалка или бэк
            // без open_doc — прежний путь (_openDocLink_legacy), см. utils/docsNav.js
            if (docsNav_open(this.store, this.WIDGET, href, { kind: 'link', fallback: () => this._openDocLink_legacy(href) })) return
            this._openDocLink_legacy(href)
        },
        // IE 2026 09 28 / ODOO-342342: прежний путь ссылки docs: (до правки — тело _openDocLink) — панель документации widget-docs;
        // им же откатывается листание на месте, когда бэк не знает open_doc
        _openDocLink_legacy(href) {
            const vueObj = this.WIDGET?.vueObj
            if (!vueObj || !href) return

            // Если мы уже ВНУТРИ виджета документации — закрываем его перед
            // открытием следующего документа. Иначе widget_open находит открытый
            // виджет по props и лишь активирует его, не переинициализируя (та же
            // ловушка, что чинили для widget-search), и документ не меняется.
            // `reload` тоже не подходит: он перечитывает запись из базы и стирает
            // вычисленные при инициализации поля (текст документа).
            const W = this.WIDGET
            let opener = vueObj
            if (W.widget === 'widget-docs' && W.id != null) {
                // Открывать новый документ надо ОТ ЧУЖОГО виджета: свой мы сейчас
                // закроем, и его vueObj станет негодным для widget_open.
                const parentId = W.parentWIDGET?.id
                const parent = parentId != null ? this.store.findWidget(parentId)?.WIDGET : null
                if (parent?.vueObj) opener = parent.vueObj
                // именно ЖЁСТКОЕ закрытие: после мягкого widget_open находит
                // «_softClosed» виджет и восстанавливает его вместе со СТАРЫМ
                // текстом, вместо того чтобы открыть новый документ
                this.store.widget_close(W.id, false, true)
            }
            this.store.executeStoreMethod(opener, {
                command: 'widget_open',
                widget: 'widget-docs',
                params: { doc_link: href },
            })
        },
        // IE 2026 09 28 / ODOO-342342: сырой HTML превью (плитки <a href="docs:…"> — html_block уходит в innerHTML) — браузер открывал
        // схему docs: как адрес. Делегированный клик уводит её в ту же навигацию, что markdown-ссылки (у тех href снят, клик свой)
        _onPreviewClick(e) {
            if (e.defaultPrevented || e.button !== 0 || !docsNav_on(this.store)) return
            const a = e.target?.closest?.('a[href^="docs:"]')
            if (!a || !e.currentTarget?.contains?.(a)) return
            docsNav_count('tileClicks')
            this._openDocLink(e, a.getAttribute('href').slice(5))
        },
        // IE 2026 09 28 / ODOO-342342: кнопки читалки Back / Contents (set_commandPanel); не вышло листать на месте — прежний путь ссылки
        onDocsBack() {
            // IE 2026 09 28 / ODOO-342342: + local — возврат к месту того же документа (переход к разделу из меню) без запроса: снять доводку прокрутки к якорю
            docsNav_back(this.store, this.WIDGET, { fallback: (link) => this._openDocLink_legacy(link), local: () => this.scrollToAnchor() })
        },
        onDocsContents() {
            // IE 2026 09 28 / ODOO-342342: меню читалки включено — «Contents» открывает его форму (группа Contents — первая), прежний путь — оглавление руководства
            if (this.docsMenuOn && typeof this.$refs.docsMenu?.show === 'function') { this.$refs.docsMenu.show(); return }
            docsNav_contents(this.store, this.WIDGET, { fallback: (link) => this._openDocLink_legacy(link) })
        },
        // IE 2026 09 28 / ODOO-342342: меню читалки (форма MenuPanel). Выбор пункта-документа: раздел ТОГО ЖЕ документа — переход на месте без запроса
        // (docsNav_jump, Back возвращает); другой документ — листание на месте (docsNav_open, Back возвращает), нельзя — прежний путь ссылки; команда — как ≡
        docsMenuPick(raw, ev) {
            if (!raw || raw.disabled) return
            const d = raw._docs
            if (!d || !d.link) { this.store.executeStoreMethod(this.WIDGET.vueObj, raw, this.attr, null, { target: (ev && ev.target) || this.$el }); return }
            docsSearch_count('picks')
            const link = docsNav_norm(d.link)
            const [path, frag = ''] = link.split('#')
            if (path && path === docsNav_path(this.WIDGET?.record?.doc?.doc_link) && docsNav_jump(this.store, this.WIDGET, frag)) {
                this.scrollToAnchor()   // к разделу; без якоря — только снять доводку прежнего (к началу ведёт docsNav_jump)
                return
            }
            if (docsNav_open(this.store, this.WIDGET, link, { kind: 'menu', fallback: () => this._openDocLink_legacy(link) })) return
            this._openDocLink_legacy(link)
        },
        docsMenuCurrentOf(raw) { return docsMenu_isPage(raw, this.WIDGET?.record?.doc?.doc_link) },
        docsMenuOpened() {
            docsSearch_count('opens')
            this._docsIndexEnsure(getLanguage())
        },
        // запросы поиска формы → «Found in text»: индекс языка интерфейса; ничего не нашлось — индекс en (запасной), группа с нейтральной пометкой.
        // Индекса ещё нет — запрос уходит после расчёта (не из вычисления), группа появится с ответом (docsIndexEpoch)
        docsSearchExtra(queries) {
            void this.docsIndexEpoch
            const lang = getLanguage()
            const t = (s, v) => this.t(s, v)
            const idx = docsSearch_cached(this.store, lang)
            if (!idx) { this._docsIndexLater(lang); return [] }
            // IE 2026 10 02 / ODOO-345043 (заказ Игоря: поиск — лишний символ в запросе): последняя ступень лестницы (итог tier ≥ 7) — выключатель fe.search.extraSymbolTier; найденное только ею уступает
            // точному / приближённому совпадению запасного индекса en (порядок прежних ступеней не меняется)
            let exOn = true
            try { exOn = this.store.setting('fe.search.extraSymbolTier', true) !== false } catch (e) { /* без стора — вкл. */ }
            let res = docsSearch_find(idx, queries, { extra: exOn }), note = ''
            if ((!res.found || res.tier >= 7) && lang !== 'en') {
                const en = docsSearch_cached(this.store, 'en')
                if (!en) this._docsIndexLater('en')
                else {
                    const r2 = docsSearch_find(en, queries, { extra: exOn })
                    // IE 2026 09 28 / ODOO-342342: страница откроется на языке интерфейса — якоря разделов его индекса (docsSearch_localize)
                    if (r2.found && (!res.found || r2.tier < 7)) { res = docsSearch_localize(r2, en, idx); note = t('Matches in other languages'); docsSearch_noteFallback() }
                }
            }
            const g = docsMenu_textGroup(res, t, { note })
            return g ? [g] : []
        },
        _docsIndexEnsure(lang) {
            return docsSearch_ensure(this.store, this.WIDGET, lang || getLanguage(), () => { this.docsIndexEpoch++ })
        },
        _docsIndexLater(lang) {
            const l = lang || getLanguage()
            this._docsIndexQueued = this._docsIndexQueued || {}
            if (this._docsIndexQueued[l]) return
            this._docsIndexQueued[l] = true
            setTimeout(() => { this._docsIndexQueued[l] = false; if (this.docsMenuOn) this._docsIndexEnsure(l) }, 0)
        },
        _walkInline(tokens) {
            const result = []
            const childrenStack = [result]
            const tagStack = []
            for (const token of (tokens || [])) {
                const current = childrenStack[childrenStack.length - 1]
                if (token.nesting === 1) {
                    const kids = []
                    tagStack.push({ tag: token.tag, attrs: token.attrs, kids })
                    childrenStack.push(kids)
                } else if (token.nesting === -1) {
                    childrenStack.pop()
                    const info = tagStack.pop()
                    const parent = childrenStack[childrenStack.length - 1]
                    const props = this._attrsToProps(info.attrs)
                    if (info.tag === 'a') {
                        // Схема `docs:` — ссылка на СОСЕДНИЙ документ репозитория
                        // (её ставит бэкенд, widgets.docs_rewrite, вместо
                        // относительного пути: браузер резолвил бы его от адреса
                        // SPA и получал 404). Открываем в ТОМ ЖЕ виджете, чтобы
                        // навигация по документации не выбрасывала из платформы.
                        const docHref = props.href || ''
                        if (docHref.startsWith('docs:')) {
                            props.href = undefined
                            props.class = [props.class, 'attrmd-doc-link'].filter(Boolean)
                            props.onClick = (e) => this._openDocLink(e, docHref.slice(5))
                        } else { props.target = '_blank'; props.rel = 'noopener' }
                    }
                    parent.push(h(info.tag, props, info.kids))
                } else {
                    if (token.type === 'text') current.push(token.content)
                    else if (token.type === 'softbreak') current.push('\n')
                    else if (token.type === 'hardbreak') current.push(h('br'))
                    else if (token.type === 'code_inline') current.push(h('code', token.content))
                    else if (token.type === 'image') {
                        const src = token.attrs?.find(a => a[0] === 'src')?.[1] || ''
                        current.push(h('img', { src, alt: token.content }))
                    } else if (token.type === 'html_inline') {
                        current.push(h('span', { innerHTML: token.content }))
                    }
                }
            }
            return result
        },
        _findAttrByName(name) {
            const walk = (attrs) => {
                if (!Array.isArray(attrs)) return null
                for (const attr of attrs) {
                    if (attr.name === name) return attr
                    const found = walk(attr.attrs) || walk(attr.groups)
                    if (found) return found
                }
                return null
            }
            return walk(this.WIDGET.view)
        },
        _buildVNodes(tokens) {
            const result = []
            const childrenStack = [result]
            const tagStack = []
            for (const token of tokens) {
                const current = childrenStack[childrenStack.length - 1]
                if (token.nesting === 1) {
                    const kids = []
                    if (token.type === 'container_alert_open') {
                        const m = token.info?.trim().match(/^alert\{type=(\w+)\}$/)
                        tagStack.push({ tag: 'div', extraClass: `attrmd-alert attrmd-alert-${m ? m[1] : 'info'}`, attrs: null, kids })
                    } else {
                        tagStack.push({ tag: token.tag, attrs: token.attrs, extraClass: null, kids })
                    }
                    childrenStack.push(kids)
                } else if (token.nesting === -1) {
                    childrenStack.pop()
                    const info = tagStack.pop()
                    const parent = childrenStack[childrenStack.length - 1]
                    const props = this._attrsToProps(info.attrs)
                    if (info.extraClass) props.class = info.extraClass
                    parent.push(h(info.tag, props, info.kids))
                } else {
                    if (token.type === 'inline') {
                        current.push(...this._walkInline(token.children))
                    } else if (token.type === 'fence') {
                        const lang = token.info.trim().split(/\s/)[0]
                        let codeChild
                        if (lang && Prism.languages[lang]) {
                            const highlighted = Prism.highlight(token.content, Prism.languages[lang], lang)
                            codeChild = h('code', { class: `language-${lang}`, innerHTML: highlighted })
                        } else {
                            codeChild = h('code', lang ? { class: `language-${lang}` } : {}, token.content)
                        }
                        current.push(h('pre', lang ? { class: `language-${lang}` } : {}, [codeChild]))
                    } else if (token.type === 'code_block') {
                        current.push(h('pre', {}, [h('code', {}, token.content)]))
                    } else if (token.type === 'hr') {
                        current.push(h('hr'))
                    } else if (token.type === 'html_block') {
                        current.push(h('div', { innerHTML: token.content }))
                    } else if (token.type === 'attr_embed') {
                        const attr = this._findAttrByName(token.attrName)
                        if (attr?.component) {
                            current.push(h(attr.component, { widget_id: this.widget_id, attr_id: attr.id }))
                        } else {
                            current.push(h('div', { class: 'md-attr-missing' }, this.t('[attr "{name}" not found]', { name: token.attrName })))   // IE 2026 09 23 / ODOO-341407: i18n
                        }
                    } else if (token.type === 'record_embed') {
                        current.push(h(this._RecordChipComponent, {
                            recordData: token.recordData,
                            widget_id: this.widget_id,
                        }))
                    }
                }
            }
            return result
        },
        set_commandPanel() {
            const commandPanel = []
            const eval_params = { attr: this.attr }

            // IE 2026 09 28 / ODOO-342342: читалка документации (fe.help.inPlaceDocs) — «Back» к предыдущему документу, если листали, и
            // «Contents» — оглавление руководства (docs/user-guide/index.md) у документов руководства; бэк без open_doc — кнопок нет
            if (this.mode === 'view') {
                const docsNav = docsNav_buttons(this.store, this.WIDGET)
                if (docsNav.back) commandPanel.push({
                    'attr_type': 'AttrButton', source_name: this.attr.name, 'binding': this.attr.binding,
                    'title': this.t('Back'), 'tooltip': this.t('Back to the previous document'), 'showCommandTitle': true,
                    'command': 'executeVueObjMethod', 'method': 'onDocsBack',
                    'cssClass': 'pi p-button-white pi-arrow-left',
                })
                // IE 2026 09 28 / ODOO-342342: меню читалки включено — «Contents» открывает его форму (разделы документа, все инструкции, поиск): есть, когда есть разделы
                // или оглавление руководства; иначе — как было (оглавление руководства)
                const menu = this.docsMenuOn
                if (menu ? (this.docsHeadings.length > 0 || !!docsNav_contentsLink(this.WIDGET)) : docsNav.contents) commandPanel.push({
                    'attr_type': 'AttrButton', source_name: this.attr.name, 'binding': this.attr.binding,
                    'title': this.t('Contents'), 'tooltip': menu ? this.t('Contents, all guides and search') : this.t('Contents: all guides'), 'showCommandTitle': true,   // IE 2026 09 28 / ODOO-342342
                    'command': 'executeVueObjMethod', 'method': 'onDocsContents',
                    'cssClass': 'pi p-button-white pi-list',
                })
            }

            if (!this.isReadOnly) {
                commandPanel.push({
                    'attr_type': 'AttrButton', source_name: this.attr.name, 'binding': this.attr.binding,
                    'active': this.mode === 'edit',
                    'title': this.mode === 'view' ? this.t('Edit markdown') : this.t('Preview'),   // IE 2026 09 23 / ODOO-341407: i18n
                    'command': 'executeVueObjMethod', 'method': 'toggleMode',
                    'cssClass': `pi p-button-white ${this.mode === 'view' ? 'pi-pencil' : 'pi-eye'}`,
                })
            }

            let need_separator = commandPanel.length
            if (this.attr?.groupMenu?.commandPanel) {
                this.attr.groupMenu.commandPanel.forEach(attrButton => {
                    const v_if = attrButton['v-if']
                    if (!attrButton.autoGeneratedButton) {
                        const visible_ = this.store.eval_value(this.WIDGET, v_if, eval_params)
                        if (attrButton.visible != visible_) {
                            attrButton.visible = visible_
                        }
                        if (need_separator) {
                            commandPanel.push({ autoGeneratedButton: true })
                            need_separator = false
                        }
                        commandPanel.push(attrButton)
                    }
                })
            }

            this.store.prepare_attrs(commandPanel, (this.attr.html_id || this.attr.id || 'md') + (++this.ind_commandPanel))
            this.commandPanel = commandPanel
            this.actionMenu = this.store.actionMenu_from_commandPanel(commandPanel)

            this._cpItemWidths = null
            this.commandPanelMaxVisible = Infinity
            this.commandPanelAreaWidth = null
            this.commandPanelMenuWidth = null
            this.$nextTick(() => {
                const container = this.$refs.commandPanelMenu
                const topLine = this.$refs.attr_top_line
                if (!container || !topLine) return
                if (!this.commandPanelResizeObserver) {
                    this.commandPanelResizeObserver = new ResizeObserver(() => this.updateCommandPanelOverflow())
                }
                this.commandPanelResizeObserver.observe(topLine)
                const chipsEl = topLine.querySelector('.grid-chips')
                if (chipsEl) this.commandPanelResizeObserver.observe(chipsEl)
                this.updateCommandPanelOverflow()
            })
        },
        onChipsUpdate(chips) {
            this.chips_list = chips
            this.apply_CHIPS()
        },
        updateCommandPanelOverflow() {
            if (this._cpRafId) cancelAnimationFrame(this._cpRafId)
            this._cpRafId = requestAnimationFrame(() => {
                this._cpRafId = null
                const cpArea = this.$refs.commandPanelArea
                const topLine = this.$refs.attr_top_line
                if (!cpArea || !topLine) return
                if (topLine.clientWidth <= 10) return
                let itemWidths = this._cpItemWidths
                if (!itemWidths?.length || itemWidths.every(w => w === 0)) {
                    const container = this.$refs.commandPanelMenu
                    itemWidths = container ? Array.from(container.children).map(el => el.offsetWidth) : []
                    if (!itemWidths.length || itemWidths.every(w => w === 0)) return
                    this._cpItemWidths = itemWidths
                }
                const chipsEl = topLine.querySelector('.grid-chips')
                let chipsDesiredWidth = 0
                if (chipsEl) {
                    chipsDesiredWidth = 130
                    const inputContainer = chipsEl.querySelector('.input-container')
                    if (inputContainer) {
                        const children = Array.from(inputContainer.children)
                        const sumW = children.reduce((s, el) => s + el.offsetWidth, 0)
                        chipsDesiredWidth = Math.max(sumW + Math.max(children.length - 1, 0) * 4 + 8, 130)
                    }
                }
                const chipsCurrentWidth = chipsEl?.clientWidth ?? 0
                const available = Math.max(cpArea.clientWidth + chipsCurrentWidth - chipsDesiredWidth, 0)
                let total = 0
                let maxVisible = itemWidths.length
                for (let i = 0; i < itemWidths.length; i++) {
                    total += itemWidths[i]
                    if (total > available) { maxVisible = i; break }
                }
                let overflowBtnWidth = 0
                if (maxVisible < itemWidths.length) {
                    const overflowBtnEl = cpArea.querySelector('.commandPanel-overflow-btn')
                    overflowBtnWidth = overflowBtnEl ? overflowBtnEl.offsetWidth : 28
                    const availableForItems = Math.max(available - overflowBtnWidth, 0)
                    total = 0
                    maxVisible = itemWidths.length
                    for (let i = 0; i < itemWidths.length; i++) {
                        total += itemWidths[i]
                        if (total > availableForItems) { maxVisible = i; break }
                    }
                }
                if (this.commandPanelMaxVisible !== maxVisible) this.commandPanelMaxVisible = maxVisible
                let visibleItemsWidth = itemWidths.slice(0, maxVisible).reduce((a, b) => a + b, 0)
                let visibleWidth = visibleItemsWidth + overflowBtnWidth
                if (!this.commandPanelOverflow.length) { visibleItemsWidth += 10; visibleWidth += 10 }
                if (this.commandPanelMenuWidth !== visibleItemsWidth) this.commandPanelMenuWidth = visibleItemsWidth
                if (this.commandPanelAreaWidth !== visibleWidth) this.commandPanelAreaWidth = visibleWidth
            })
        },
        menuNodeClicked(menu) {
            const action = menu.itemsSource[menu.selectedIndex]
            const vueObj = this.WIDGET.vueObj
            this.store.executeStoreMethod(vueObj, action)
        },
        attrResize() {
            if (this.attrResize_timeout) { clearTimeout(this.attrResize_timeout) }
            this.attrResize_timeout = window.$cg.setTimeout(this, "AttrMD.attrResize", () => {
                this.attrResize_afterTimeout()
                this.attrResize_timeout = null
            }, 1)
        },
        attrResize_afterTimeout() {
            if (!this.editor) return
            if (this.attr?.noAutoResize) return
            const elementForm = document.getElementById(`WIDGET${this.widget_id}`)
            if (!elementForm) return
            const wrapperEl = this.editor.getWrapperElement()
            const wrapperRect = wrapperEl.getBoundingClientRect()
            if (wrapperRect.height <= 10) return
            const widgetAttrsEl = elementForm.querySelector('.widget-attrs')
            const containerRect = (widgetAttrsEl || elementForm).getBoundingClientRect()
            let newHeight = Math.floor(containerRect.bottom - wrapperRect.top - 3)
            if (newHeight > containerRect.height) newHeight = 300
            if (newHeight < 80) newHeight = 80
            if (wrapperEl.style.height !== `${newHeight}px`) {
                this.editor.setSize(null, newHeight)
            }
        },
    },
    computed: {
        // IE 2026 09 28 / ODOO-342342: меню читалки — форма MenuPanel справа: читалка документации, просмотр, флаг fe.help.docsSearch, панели-меню не выключены
        // (fe.ux.menuPanel) и не мобильный профиль; иначе — прежнее ≡
        docsMenuOn() {
            void this.store?._settingsEpoch
            if (this.mode !== 'view' || !this.WIDGET || !docsNav_isDocs(this.WIDGET) || !docsSearch_on(this.store)) return false
            return typeof this.store.menuPanel_on !== 'function' || this.store.menuPanel_on()
        },
        docsHeadings() { return this.docsMenuOn ? docsSearch_headings(this.content_str) : [] },   // IE 2026 09 28 / ODOO-342342: разделы открытого документа
        // IE 2026 09 28 / ODOO-342342: группы формы: Contents (разделы документа) · «Все инструкции» (индекс бэка) · Command panel (без «Contents»)
        docsMenuItems() {
            if (!this.docsMenuOn) return []
            void this.docsIndexEpoch
            const t = (s, v) => this.t(s, v)
            const out = []
            const toc = docsMenu_contentsGroup(this.docsHeadings, docsNav_path(this.WIDGET?.record?.doc?.doc_link), t)
            if (toc) out.push(toc)
            const guides = docsMenu_guidesGroup(docsSearch_cached(this.store, getLanguage()), this.WIDGET, t)
            if (guides) out.push(guides)
            out.push(...docsMenu_commandGroups(this.actionMenu))
            return out
        },
        docsMenuSubtitle() { return String(this.WIDGET?.title || '') },   // IE 2026 09 28 / ODOO-342342: к чему меню — заголовок документа
        docsTopLineStyle() { return (this.docsMenuOn && this.docsStickyTop > 0) ? { top: this.docsStickyTop + 'px' } : null },   // IE 2026 09 28 / ODOO-342342: строка шапки — под полосой вкладок
        isReadOnly() {
            // виджет/attr могут отсутствовать, пока стол пересоздаётся (смена процессной модели)
            return this.WIDGET?.isReadOnly || this.attr?.isReadOnly || this.attr?.fieldMapping?.use || false
        },
        commandPanelOverflow() {
            if (this.commandPanelMaxVisible >= this.commandPanel.length) return []
            return this.commandPanel
                .slice(this.commandPanelMaxVisible)
                .filter(item => item.attr_type !== 'AttrSeparator' && item.visible !== false)
        },
    },
    watch: {
        content_str() {
            this.set_AttrTabPanel_countItems()
        },
    },
    components: {
        Codemirror,
    },
}
</script>

<style>
/* Unscoped styles for v-html rendered content */
.attrmd-preview {
    padding: 0.75rem;
    line-height: 1.6;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.attrmd-preview h1,
.attrmd-preview h2,
.attrmd-preview h3,
.attrmd-preview h4,
.attrmd-preview h5,
.attrmd-preview h6 {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    font-weight: 600;
    line-height: 1.3;
}
.attrmd-preview h1 { font-size: 1.75rem; border-bottom: 1px solid var(--surface-border); padding-bottom: 0.3rem; }
.attrmd-preview h2 { font-size: 1.5rem; border-bottom: 1px solid var(--surface-border); padding-bottom: 0.3rem; }
.attrmd-preview h3 { font-size: 1.25rem; }
.attrmd-preview h4 { font-size: 1.1rem; }

.attrmd-preview p {
    margin: 0.5rem 0;
}

.attrmd-preview ul,
.attrmd-preview ol {
    padding-left: 1.5rem;
    margin: 0.5rem 0;
}

.attrmd-preview blockquote {
    margin: 0.5rem 0;
    padding: 0.5rem 1rem;
    border-left: 4px solid var(--surface-border);
    color: var(--text-color-secondary);
    background: var(--surface-ground);
}

/* Tables */
.attrmd-preview table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.5rem 0;
}
.attrmd-preview th,
.attrmd-preview td {
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--surface-border);
    text-align: left;
}
.attrmd-preview th {
    background: var(--surface-ground);
    font-weight: 600;
}

/* Inline code */
.attrmd-preview code:not([class*="language-"]) {
    background: var(--surface-ground);
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
    font-size: 0.9em;
}

/* Code blocks */
.attrmd-preview pre {
    margin: 0.5rem 0;
    border-radius: 6px;
    overflow-x: auto;
}

/* Alert blocks */
.attrmd-alert {
    padding: 0.75rem 1rem;
    margin: 0.5rem 0;
    border-radius: 6px;
    border-left: 4px solid;
}
.attrmd-alert-danger {
    background: var(--red-50);
    border-color: var(--red-500);
    color: var(--red-700);
}
.attrmd-alert-warning {
    background: var(--yellow-50);
    border-color: var(--yellow-500);
    color: var(--yellow-700);
}
.attrmd-alert-info {
    background: var(--blue-50);
    border-color: var(--blue-500);
    color: var(--blue-700);
}

/* Task lists / checklists */
.attrmd-preview .task-list-item {
    list-style: none;
    margin-left: -1.5rem;
}
.attrmd-preview .task-list-item input[type="checkbox"] {
    margin-right: 0.5rem;
}

/* Horizontal rule */
.attrmd-preview hr {
    border: none;
    border-top: 1px solid var(--surface-border);
    margin: 1rem 0;
}

/* Images */
.attrmd-preview img {
    max-width: 100%;
    height: auto;
}

/* Links */
.attrmd-preview a {
    color: var(--primary-color);
    text-decoration: none;
}
.attrmd-preview a:hover {
    text-decoration: underline;
}
/* Ссылка на соседний документ: href нет (открываем виджетом), поэтому
   курсор-руку и подчёркивание задаём явно — иначе выглядит как обычный текст. */
.attrmd-preview a.attrmd-doc-link {
    cursor: pointer;
    border-bottom: 1px dashed var(--primary-color);
}

.md-record-chip {
    display: inline-flex;
    align-items: center;
    background: rgb(222 226 230 / 75%);
    color: var(--text-color);
    border-radius: 3px;
    font-size: 14px;
    white-space: nowrap;
    user-select: none;
    vertical-align: middle;
}
.md-record-chip-icon {
    background: rgba(119, 136, 153, 0.8);
    padding: 4px;
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
    color: white;
    cursor: pointer;
    font-size: 12px;
}
.md-record-chip-icon:hover {
    background: rgba(80, 95, 110, 0.9);
}
.md-record-chip-text {
    margin: 0 6px 0 4px;
    cursor: pointer;
}
.md-record-chip-text:hover {
    text-decoration: underline;
}

.md-attr-missing {
    display: inline-block;
    color: var(--red-500);
    padding: 2px 8px;
    border: 1px dashed var(--red-300);
    border-radius: 4px;
    font-style: italic;
    font-size: 0.85em;
}

/* IE 2026 09 28 / ODOO-342342 (Igor: «"меню" — должно быть прижато вправо»): ≡ читалки документации — у правого края строки шапки; в форме меню текущая страница — полужирным */
.attrmd-docs-menu-host { margin-left: auto; }
.attrmd-docs-menu .menu-panel-item.is-current > .menu-panel-text { font-weight: 600; }
.md-top-line {
    position: sticky;
    top: 0;
    z-index: 10;
    background: white;
    flex-shrink: 0;
    opacity: 1;
}
.attr-md .codemirror-container {
    margin-top: 4px;
}
</style>
